import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { BaseStampTool, StampStyles } from "./BaseStampTool";

/** A custom PDFTron tool to stamp signatures. */
class SignatureTool extends BaseStampTool {
    constructor(viewer: Core.DocumentViewer, signatureBase64: string, width: number, height: number) {
        super(viewer, "SignatureTool", width, height);
        this.base64Image = signatureBase64;
    }
}

/** Create the signature tool and register it to PDFTron document viewer. */
async function createSignatureTool(documentViewer: Core.DocumentViewer): Promise<void> {
    const signatureBytes = settingsStore.configuration.get("SignatureByteArray");
    if (sessionStore.hasPinSet && signatureBytes) {
        const signatureBase64 = `data:${MyleHelper.GetImageMime(signatureBytes)};base64,${signatureBytes}`;
        const completeSignature = await createCompleteSignature.call(this, signatureBase64);
        documentViewer.getToolModeMap().SignatureTool = new SignatureTool(documentViewer, completeSignature.signatureBase64, completeSignature.width, completeSignature.height);
    }
}

/** Add signature footer. */
async function createCompleteSignature(signatureBase64: string): Promise<{ signatureBase64: string, width: number, height: number }> {
    const employee = sessionStore.getCurrentEmployee();
    const licenseNumber = employee.Model.Licences?.firstOrDefault()?.LicenceNumber;
    const fullName = `${employee.nameWithPrefix()}${licenseNumber ? ` (${licenseNumber})` : ""}`;
    const title = LicenceOrganisationRule.getTitleName(Employee.getIssuingOrgRule(employee.Model));
    const dateTime = LuxonDateHelper.formatToFullDateTime(new Date());

    const img = await loadImage(signatureBase64);
    const imgRatio = img.width / img.height;
    const lineHeight = 15;
    const maxStringLength = 32;
    const defaultWidth = 200;

    const imgWidth = imgRatio > 1 ? defaultWidth : (defaultWidth * imgRatio);
    const imgHeight = imgRatio > 1 ? (defaultWidth / imgRatio) : defaultWidth;

    const canvas = document.createElement("canvas");
    const canvas2DContext = canvas.getContext("2d");

    // Split the fullname in case it exceeds the maximum string length
    const splittedFullName = MyleHelper.splitString(fullName, maxStringLength);
    const footerTextList = splittedFullName.concat([title, dateTime]);
    const footerHeight = (footerTextList.length + 1) * lineHeight;

    const signatureHeight = imgHeight + footerHeight;

    canvas2DContext.canvas.width = defaultWidth;
    canvas2DContext.canvas.height = signatureHeight;

    canvas2DContext.fillStyle = StampStyles.background;
    canvas2DContext.fillRect(0, 0, canvas.width, canvas.height);

    canvas2DContext.drawImage(img, (defaultWidth - imgWidth) / 2, 0, imgWidth, imgHeight);

    canvas2DContext.beginPath();
    canvas2DContext.lineWidth = 1;
    canvas2DContext.strokeStyle = StampStyles.primaryColor;
    canvas2DContext.moveTo(0, imgHeight);
    canvas2DContext.lineTo(defaultWidth, imgHeight);
    canvas2DContext.stroke();

    canvas2DContext.beginPath();
    canvas2DContext.fillStyle = StampStyles.primaryColor;
    canvas2DContext.font = `10.5pt ${StampStyles.fontFamily}`;

    let imgHeightPad: number = 0;
    for (const text of footerTextList) {
        imgHeightPad += lineHeight;
        const textWidth = canvas2DContext.measureText(text).width;
        const textX = defaultWidth - textWidth;
        const textY = imgHeight + imgHeightPad;
        canvas2DContext.fillText(text, textX, textY);
    }

    const completeSignatureBase64 = canvas.toDataURL("image/png");

    return {
        signatureBase64: completeSignatureBase64,
        width: defaultWidth,
        height: signatureHeight
    };
}

/** A utility tool to load an image into the DOM. */
function loadImage(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>(
        resolve => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = imageUrl;
        }
    );
}

export { createSignatureTool };
